import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import {
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { setCachedUserProfile } from '../services/userProfileService';
import { shouldUpdateLastVisit } from '../services/lastVisit';

import {
  identifyUser,
  initAmplitude,
  resetUser,
  setAnalyticsContext,
} from '../amplitude';

const UserContext = createContext();

export const useUser = () => {
  return useContext(UserContext);
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [userData, setUserData] = useState(undefined);
  const [loadingUser, setLoadingUser] = useState(true);
  const updateCurrentUserProfile = useCallback((updates) => {
    setUserData((current) => current ? { ...current, ...updates } : current);
  }, []);

  useEffect(() => {
    let unsubscribeProfile = null;
    initAmplitude();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        unsubscribeProfile?.();
        unsubscribeProfile = onSnapshot(
          doc(db, 'users', currentUser.uid),
          (docSnap) => {
            const fetchedData = docSnap.exists() ? docSnap.data() : null;
            if (fetchedData) {
              const storageKey = `bigSport:lastVisit:${currentUser.uid}`;
              const lastWrite = sessionStorage.getItem(storageKey);
              if (shouldUpdateLastVisit(lastWrite)) {
                sessionStorage.setItem(storageKey, String(Date.now()));
                updateDoc(doc(db, 'users', currentUser.uid), {
                  lastVisitAt: serverTimestamp(),
                }).catch((error) => {
                  sessionStorage.removeItem(storageKey);
                  if (process.env.NODE_ENV === 'development') {
                    console.error('Не вдалося оновити lastVisitAt:', error);
                  }
                });
              }

              setCachedUserProfile(currentUser.uid, {
                id: currentUser.uid,
                ...fetchedData,
              });
            }
            setUserData(fetchedData);
            setLoadingUser(false);

            if (fetchedData?.isDeleted || fetchedData?.deletionPending) {
              setUserData(null);
            } else {
              identifyUser(currentUser.uid, {
                ...fetchedData,
                displayName:
                  fetchedData?.displayName
                  || fetchedData?.username
                  || currentUser.displayName,
                email: fetchedData?.email || currentUser.email,
                authProvider: currentUser.providerData?.[0]?.providerId,
              });
            }
          },
          (error) => {
            console.error('Помилка отримання userData:', error);
            setUserData(null);
            setLoadingUser(false);
          }
        );
      } else {
        unsubscribeProfile?.();
        unsubscribeProfile = null;
        setUserData(null);
        setLoadingUser(false);
        setAnalyticsContext({ isAuthenticated: false, role: 'guest' });
        resetUser();
      }
    });

    return () => {
      unsubscribe();
      unsubscribeProfile?.();
    };
  }, []);

  return (
    <UserContext.Provider
      value={{ user, userData, loadingUser, updateCurrentUserProfile }}
    >
      {children}
    </UserContext.Provider>
  );
};
